import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelProps,
  type ElementSizingMode,
  type ElementTitleStyleProps,
  getActiveBinding,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type Nullish, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { IFrameComponent } from './iframe.component';
import { IFramePropsBuilder } from './iframe.props-builder';
import { IFramePropsExtractor } from './iframe.props-extractor';
import { iframeV2Migration } from './migrations/002-iframe.migration';

export { transformToIframe } from './iframe.transforms';
export type IFrameDef = WithProps<IFramePropsModel> & WithIoConfig;
export type IFrameElement = CanvasElementFrom<'iframe'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    iframe: IFrameDef;
  }
}

const DEFAULT_WIDTH = 160;
const DEFAULT_HEIGHT = 300;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];

registerElementDefinition({
  type: 'iframe',
  componentType: IFrameComponent,
  isListed: true,
  name: 'IFrame',
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: IFramePropsBuilder,
  propsExtractor: IFramePropsExtractor,
  defaults: getDefaults(),
  tags: ['website', 'webpage', 'link', 'iframe', 'url'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  features: {
    visualStyle: 'block',
  },
  version: 2,
  migrations: {
    2: iframeV2Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type IFramePropsModel = PropsModel & {
  content: {
    iframe: {
      enabled: boolean;
      url: string;
    };
    title: ElementLabelProps;
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    titleBlock: ElementTitleStyleProps;
    label: {
      color: PropertiesConfigInputColorModel;
    };
    border: {
      outline: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
      width: ValueWithUnitOfMeasure;
    };
    container: {
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
    };
    background: {
      fill: PropertiesConfigInputColorModel;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
  };
};

function getDefaults(): PropsOf<'iframe'> {
  return {
    content: {
      iframe: {
        enabled: true,
        url: '',
      },
      title: {
        enabled: false,
        align: 'left',
        label: 'IFrame',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      label: {
        color: { color1: '$t.title', selectorType: 'solid' },
      },
      border: {
        outline: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
        width: '0px',
      },
      container: {
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        outlineWidth: '$t.containerOutlineWidth',
      },
      background: {
        fill: { color1: '$t.fillExternal', selectorType: 'solid' },
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('iframe', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.iframe',
        'Source',
        [
          ...epc.utils.disabledFields(
            [
              epc.fields.textInput('content.iframe.url', 'URL', {
                placeholder: 'https://blaze.tech',
                onBlur: onUrlBlur,
                onFocus: onUrlFocus,
              }),
            ],
            (model) => {
              return getActiveBinding(model.data, 'value')
                ? 'The Value of the element is bound'
                : undefined;
            },
          ),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'URL',
              sources: 'field',
              bindings: [{ property: 'value', label: 'URL', dataType: 'String' }],
            },
          ],
        },
      ),
      epc.subsections.subsection('content.iframe', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.general', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),

        epc.subsections.subsection('style.border', 'Border', [
          epc.fields.colorField('style.border.outline', 'Outline', {
            allowGradient: false,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.border.width', 'Width', [{ min: 0, max: 25, unit: 'px' }]),
          epc.fields.radiusField('style.border.radius'),
        ]),

        epc.subsections.subsection('style.background', 'Background', [
          epc.fields.colorField('style.background.fill', 'Fill'),
          ...epc.fields.visualEffectField('style.background.visualEffect'),
        ]),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.label.color',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

function onUrlBlur(model: Nullish<string>): Nullish<string> {
  if (model === 'https://') {
    model = '';
  } else if (model && !model.startsWith('http://') && !model.startsWith('https://')) {
    model = `https://${model}`;
  }
  return model;
}

function onUrlFocus(model: Nullish<string>): Nullish<string> {
  if (!model) {
    model = 'https://';
  }
  return model;
}
